import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useI18n } from "@/context/I18nContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import client, { formatApiError } from "@/lib/api";

export default function ResetPassword() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";

  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    if (newPassword !== confirmPassword) {
      setError(t("resetPassword.mismatch"));
      return;
    }
    setBusy(true);
    try {
      await client.post(`/auth/reset-password`, { token, new_password: newPassword });
      setDone(true);
      setTimeout(() => navigate("/login", { replace: true }), 2500);
    } catch (err) {
      setError(formatApiError(err?.response?.data?.detail) || t("resetPassword.invalidLink"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen w-full items-center justify-center p-6">
      <div className="w-full max-w-md">
        <Card className="border-border p-8 shadow-sm">
          <div className="mb-8">
            <h2 className="text-3xl font-bold tracking-tight">{t("resetPassword.title")}</h2>
          </div>

          {!token ? (
            <p data-testid="reset-password-invalid-link" className="text-sm text-destructive">
              {t("resetPassword.invalidLink")}
            </p>
          ) : done ? (
            <p data-testid="reset-password-success" className="text-sm text-foreground">
              {t("resetPassword.success")}
            </p>
          ) : (
            <form onSubmit={submit} className="space-y-4" data-testid="reset-password-form">
              <div className="space-y-1.5">
                <Label htmlFor="new-password" className="data-label">
                  {t("resetPassword.newPassword")}
                </Label>
                <Input
                  id="new-password"
                  data-testid="reset-password-new"
                  type="password"
                  autoComplete="new-password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="confirm-password" className="data-label">
                  {t("resetPassword.confirmPassword")}
                </Label>
                <Input
                  id="confirm-password"
                  data-testid="reset-password-confirm"
                  type="password"
                  autoComplete="new-password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                />
              </div>
              {error && (
                <p data-testid="reset-password-error" className="text-xs font-medium text-destructive">
                  {error}
                </p>
              )}
              <Button data-testid="reset-password-submit" type="submit" disabled={busy} className="w-full">
                {busy ? t("common.loading") : t("resetPassword.submit")}
              </Button>
            </form>
          )}

          <Link
            to="/login"
            data-testid="reset-password-back-link"
            className="mt-6 block text-center text-xs text-muted-foreground hover:text-foreground"
          >
            {t("resetPassword.backToLogin")}
          </Link>
        </Card>
      </div>
    </div>
  );
}
